import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExceptionManagement = () => {
  const [selectedTab, setSelectedTab] = useState('missing');

  const exceptions = {
    missing: [
      {
        id: 'AT-2025-0089',
        assetType: 'Server',
        expectedLocation: 'Data Center - Rack A3',
        lastSeen: '2024-12-01',
        reportedBy: 'Mike Davis',
        escalationLevel: 'Department Manager',
        daysOpen: 12,
        priority: 'Critical'
      },
      {
        id: 'AT-2025-0234',
        assetType: 'Laptop',
        expectedLocation: 'Floor 2 - Desk 45',
        lastSeen: '2025-01-15',
        reportedBy: 'Sarah Johnson',
        escalationLevel: 'IT Manager',
        daysOpen: 3,
        priority: 'High'
      }
    ],
    mismatch: [
      {
        id: 'AT-2025-0156',
        assetType: 'Monitor',
        expectedLocation: 'Floor 3 - Conference Room',
        actualLocation: 'Floor 1 - Reception',
        reportedBy: 'Lisa Chen',
        discrepancyType: 'Location Mismatch',
        daysOpen: 5,
        priority: 'Medium'
      },
      {
        id: 'AT-2025-0178',
        assetType: 'Printer',
        expectedLocation: 'Floor 2 - Copy Center',
        actualLocation: 'Floor 2 - Break Room',
        reportedBy: 'Tom Wilson',
        discrepancyType: 'Unauthorized Move',
        daysOpen: 2,
        priority: 'Low'
      }
    ],
    conflicts: [
      {
        id: 'AT-2025-0067',
        assetType: 'Router',
        issue: 'Status conflict between system records',
        systemStatus: 'Active',
        physicalStatus: 'Decommissioned',
        reportedBy: 'John Smith',
        conflictType: 'Status Mismatch',
        daysOpen: 7,
        priority: 'High'
      }
    ]
  };

  const tabs = [
    { key: 'missing', label: 'Missing Assets', count: exceptions?.missing?.length, icon: 'AlertCircle' },
    { key: 'mismatch', label: 'Location Mismatches', count: exceptions?.mismatch?.length, icon: 'MapPin' },
    { key: 'conflicts', label: 'Status Conflicts', count: exceptions?.conflicts?.length, icon: 'AlertTriangle' }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-error text-error-foreground';
      case 'High':
        return 'bg-warning text-warning-foreground';
      case 'Medium':
        return 'bg-accent text-accent-foreground';
      case 'Low':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleEscalate = (assetId) => {
    console.log('Escalating exception for asset:', assetId);
  };

  const handleResolve = (assetId) => {
    console.log('Resolving exception for asset:', assetId);
  };

  const renderMissingAssets = () => (
    <div className="space-y-4">
      {exceptions?.missing?.map((asset) => (
        <div key={asset?.id} className="bg-card border border-border rounded-lg p-4 shadow-subtle">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className="font-medium text-card-foreground">{asset?.id}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(asset?.priority)}`}>
                  {asset?.priority}
                </span>
                <span className="text-sm text-muted-foreground">
                  {asset?.daysOpen} days open
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Asset Type:</span>
                  <span className="ml-2 text-card-foreground">{asset?.assetType}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Expected Location:</span>
                  <span className="ml-2 text-card-foreground">{asset?.expectedLocation}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Seen:</span>
                  <span className="ml-2 text-card-foreground">{asset?.lastSeen}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Reported By:</span>
                  <span className="ml-2 text-card-foreground">{asset?.reportedBy}</span>
                </div>
              </div>
              <div className="mt-3 p-3 bg-warning/10 border border-warning/20 rounded-md">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-warning">
                    Escalated to: {asset?.escalationLevel}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Button variant="outline" size="sm" onClick={() => handleEscalate(asset?.id)}>
                <Icon name="ArrowUp" size={16} className="mr-2" />
                Escalate
              </Button>
              <Button variant="default" size="sm" onClick={() => handleResolve(asset?.id)}>
                <Icon name="CheckCircle" size={16} className="mr-2" />
                Resolve
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderLocationMismatches = () => (
    <div className="space-y-4">
      {exceptions?.mismatch?.map((asset) => (
        <div key={asset?.id} className="bg-card border border-border rounded-lg p-4 shadow-subtle">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className="font-medium text-card-foreground">{asset?.id}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(asset?.priority)}`}>
                  {asset?.priority}
                </span>
                <span className="text-sm text-muted-foreground">
                  {asset?.daysOpen} days open
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                <div>
                  <span className="text-muted-foreground">Asset Type:</span>
                  <span className="ml-2 text-card-foreground">{asset?.assetType}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Discrepancy Type:</span>
                  <span className="ml-2 text-card-foreground">{asset?.discrepancyType}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} className="text-error" />
                  <span className="text-sm text-muted-foreground">Expected:</span>
                  <span className="text-sm text-card-foreground">{asset?.expectedLocation}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} className="text-success" />
                  <span className="text-sm text-muted-foreground">Found at:</span>
                  <span className="text-sm text-card-foreground">{asset?.actualLocation}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Button variant="outline" size="sm">
                <Icon name="RotateCcw" size={16} className="mr-2" />
                Update Location
              </Button>
              <Button variant="default" size="sm" onClick={() => handleResolve(asset?.id)}>
                <Icon name="CheckCircle" size={16} className="mr-2" />
                Resolve
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderStatusConflicts = () => (
    <div className="space-y-4">
      {exceptions?.conflicts?.map((asset) => (
        <div key={asset?.id} className="bg-card border border-border rounded-lg p-4 shadow-subtle">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className="font-medium text-card-foreground">{asset?.id}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(asset?.priority)}`}>
                  {asset?.priority}
                </span>
                <span className="text-sm text-muted-foreground">
                  {asset?.daysOpen} days open
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{asset?.issue}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Icon name="Database" size={16} className="text-error" />
                    <span className="font-medium text-error">System Status</span>
                  </div>
                  <span className="text-sm text-card-foreground">{asset?.systemStatus}</span>
                </div>
                <div className="p-3 bg-success/10 border border-success/20 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Icon name="Eye" size={16} className="text-success" />
                    <span className="font-medium text-success">Physical Status</span>
                  </div>
                  <span className="text-sm text-card-foreground">{asset?.physicalStatus}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Button variant="outline" size="sm">
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Sync Status
              </Button>
              <Button variant="default" size="sm" onClick={() => handleResolve(asset?.id)}>
                <Icon name="CheckCircle" size={16} className="mr-2" />
                Resolve
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    switch (selectedTab) {
      case 'missing':
        return renderMissingAssets();
      case 'mismatch':
        return renderLocationMismatches();
      case 'conflicts':
        return renderStatusConflicts();
      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Exception Management</h3>
            <p className="text-sm text-muted-foreground">
              Manage audit discrepancies and escalation workflows
            </p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="AlertTriangle" size={16} className="mr-2" />
            Create Exception Report
          </Button>
        </div>

        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setSelectedTab(tab?.key)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                selectedTab === tab?.key
                  ? 'bg-card text-card-foreground shadow-subtle'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
              {tab?.count > 0 && (
                <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                  {tab?.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {renderContent()}
      </div>
    </div>
  );
};

export default ExceptionManagement;